/**
 * Reusable PURE presentation pieces for a saved investigation:
 *
 * - `InvestigationCard` — compact grid card. Click navigates via the
 *   `to` prop (a stable URL). The list page composes this in a grid.
 * - `InvestigationSourcePill` — small AI/User pill with optional read-only
 *   chip.
 *
 * Also exports the data helpers (`buildTimeline`, `getDescriptionText`, …) the
 * features/ hooks consume. All visuals use shadcn token classes (no hardcoded
 * colors) and this file performs no IO.
 */

import {
  ArrowRight,
  ListTodo,
  Lock,
  MoreHorizontal,
  Pin,
  Scale,
  Sparkles,
  Star,
  StickyNote,
  Trash2,
  User,
} from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { GraphNoteItem } from '@/services/api/graph'
import type { GraphViewNode, GraphViewNote } from '@/types/graph-view'
import type { Investigation, InvestigationNoteKind } from '@/types/investigation'
import { cn } from '@/utils/cn'
import { formatRelative } from '@/utils/date'
import { getEntityDisplayLabel } from '@/utils/entity-display'

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function chapterNumber(index: number): string {
  return `№ ${(index + 1).toString().padStart(2, '0')}`
}

function nodeKind(node: GraphViewNode): string {
  return node.typeLabel ?? node.labels[0] ?? 'Entity'
}

function nodeLabel(node: GraphViewNode): string {
  return getEntityDisplayLabel(node)
}

const ENTITY_KIND_PALETTE = [
  'bg-primary',
  'bg-success',
  'bg-warning',
  'bg-info',
  'bg-muted-foreground',
] as const

// ─── Source pill ─────────────────────────────────────────────────────────────

export function InvestigationSourcePill({ inv }: { inv: Investigation }) {
  const { t } = useTranslation()
  const isAi = inv.source === 'ai'
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
          isAi
            ? 'border-primary/40 bg-primary/10 text-primary'
            : 'border-border bg-muted text-muted-foreground'
        )}
      >
        {isAi ? <Sparkles className="size-3" /> : <User className="size-3" />}
        {t(isAi ? 'investigations.page.source.ai' : 'investigations.page.source.user')}
      </span>
      {inv.readOnly && (
        <span
          className="inline-flex items-center gap-1 rounded-full border border-border px-1.5 py-0.5 text-3xs text-muted-foreground"
          title={t('investigations.page.source.readOnly')}
        >
          <Lock className="size-2.5" />
          {t('investigations.page.source.readOnly')}
        </span>
      )}
    </span>
  )
}

// ─── Snapshot pill (entity-type dots + total count) ──────────────────────────

export function GridSnapshotPill({ nodes }: { nodes: GraphViewNode[] }) {
  const { t } = useTranslation()
  const distinctKinds = useMemo(() => {
    const seen = new Set<string>()
    for (const n of nodes) seen.add(nodeKind(n))
    return Array.from(seen).slice(0, 5)
  }, [nodes])

  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-border bg-muted px-2.5 py-1 text-xs text-muted-foreground">
      <span className="inline-flex gap-0.5">
        {distinctKinds.map((kind, i) => (
          <span
            key={kind}
            className={cn(
              'inline-block size-1.5 rounded-full',
              ENTITY_KIND_PALETTE[i % ENTITY_KIND_PALETTE.length]
            )}
          />
        ))}
      </span>
      <span className="font-mono tabular-nums">{nodes.length}</span>
      <span className="text-muted-foreground/70">{t('investigations.page.card.objects')}</span>
    </span>
  )
}

// ─── Description (stored as a marker note) ───────────────────────────────────
//
// The Investigation schema has no first-class `description` field. We persist
// the description by reserving a special note whose `authorName` matches
// `DESCRIPTION_NOTE_AUTHOR`. Helpers below read/write that note while
// `buildTimeline` filters it out so it never shows up in the notes feed.

export const DESCRIPTION_NOTE_AUTHOR = '__investigation_description__'

export function getDescriptionText(inv: Investigation): string {
  const note = inv.notes.find(n => n.authorName === DESCRIPTION_NOTE_AUTHOR)
  return note?.text ?? ''
}

export function notesWithDescription(
  inv: Investigation,
  text: string
): import('@/types/investigation').InvestigationNote[] {
  const others = inv.notes.filter(n => n.authorName !== DESCRIPTION_NOTE_AUTHOR)
  const trimmed = text.trim()
  if (!trimmed) return others
  const existing = inv.notes.find(n => n.authorName === DESCRIPTION_NOTE_AUTHOR)
  return [
    {
      id: existing?.id ?? `desc-${inv.id}`,
      text: trimmed,
      authorName: DESCRIPTION_NOTE_AUTHOR,
      authorId: existing?.authorId,
      createdAt: existing?.createdAt ?? new Date().toISOString(),
    },
    ...others,
  ]
}

// ─── Notes timeline ──────────────────────────────────────────────────────────

export interface TimelineEntry {
  id: string
  kind: 'investigation' | 'graph'
  /** An investigation note's kind (observation, assessment, task); a graph note has none. */
  noteKind?: InvestigationNoteKind
  text: string
  author: string
  createdAt: string
  /** First attached entity's label (legacy single-entity badge). */
  attachedEntity?: string
  /** Every entity the underlying Neo4j Note is attached to via HAS_NOTE.
   *  Populated for "graph" notes when the Neo4j note record is available;
   *  empty otherwise. Used to render one chip per linked entity. */
  attachedEntities?: { id: string; label: string }[]
}

export function buildTimeline(
  inv: Investigation,
  graphNotes: GraphNoteItem[] = []
): TimelineEntry[] {
  const investigation = inv.notes
    .filter(n => n.authorName !== DESCRIPTION_NOTE_AUTHOR)
    .map<TimelineEntry>(n => ({
      id: `inv-${n.id}`,
      kind: 'investigation',
      noteKind: n.kind ?? 'observation',
      text: n.text,
      author: n.authorName,
      createdAt: n.createdAt,
    }))

  const nodeNameById = new Map<string, string>(inv.graphView.nodes.map(n => [n.id, nodeLabel(n)]))
  const investigationNodeIds = new Set(inv.graphView.nodes.map(n => n.id))

  // Index Neo4j notes by id so each saved-view snapshot can be enriched with
  // its real HAS_NOTE edges (the snapshot field carries only position + text,
  // not connected_node_ids — those live in Neo4j).
  const noteFromNeo4jById = new Map<string, GraphNoteItem>(graphNotes.map(n => [n.id, n]))

  // Pull together two sources of graph notes:
  //   (a) snapshots inside `inv.graphView.view.notes` — these are positional
  //       records baked into the saved investigation view.
  //   (b) live Neo4j notes whose HAS_NOTE touches an entity in the object set
  //       and whose id isn't already covered by (a) — picks up notes added in
  //       exploration mode (or after the view was last saved) that legitimately
  //       belong to this investigation's context.
  // Both render as the same "GRAPH NOTE" — they're the same concept, just with
  // different freshness guarantees. Attribution chips use the Neo4j-side
  // `connected_node_ids` so the badge points at the actual HAS_NOTE peer
  // instead of the previous "first node in graphView" guess.
  const seenIds = new Set<string>()
  const graph: TimelineEntry[] = []

  for (const snapshot of inv.graphView.view.notes as GraphViewNote[]) {
    seenIds.add(snapshot.id)
    const enriched = noteFromNeo4jById.get(snapshot.id)
    const attached =
      enriched?.connected_node_ids
        .filter(id => investigationNodeIds.has(id))
        .map(id => ({ id, label: nodeNameById.get(id) ?? id })) ?? []
    graph.push({
      id: `graph-${snapshot.id}`,
      kind: 'graph',
      text: snapshot.text,
      author: enriched?.author_name ?? snapshot.authorId ?? 'Graph',
      createdAt: snapshot.createdAt,
      attachedEntity: attached[0]?.label,
      attachedEntities: attached,
    })
  }

  for (const note of graphNotes) {
    if (seenIds.has(note.id)) continue
    const attached = note.connected_node_ids
      .filter(id => investigationNodeIds.has(id))
      .map(id => ({ id, label: nodeNameById.get(id) ?? id }))
    if (attached.length === 0) continue // not connected to this investigation's set
    graph.push({
      id: `graph-${note.id}`,
      kind: 'graph',
      text: note.text,
      author: note.author_name ?? note.author_id ?? 'Graph',
      createdAt: note.created_at,
      attachedEntity: attached[0]?.label,
      attachedEntities: attached,
    })
  }

  return [...investigation, ...graph].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )
}

export function NoteRow({ entry }: { entry: TimelineEntry }) {
  const { t, i18n } = useTranslation()
  const isGraph = entry.kind === 'graph'
  const attachedChips =
    entry.attachedEntities && entry.attachedEntities.length > 0
      ? entry.attachedEntities
      : entry.attachedEntity
        ? [{ id: entry.attachedEntity, label: entry.attachedEntity }]
        : []
  return (
    <div className="relative pb-5 pl-7">
      <span className="absolute bottom-[-6px] left-[9px] top-[18px] w-px bg-border" />
      <span
        className={cn(
          'absolute left-0 top-0.5 flex size-[19px] items-center justify-center rounded-full border bg-card',
          isGraph ? 'border-primary text-primary' : 'border-border text-muted-foreground'
        )}
      >
        {isGraph ? <Pin className="size-2.5" /> : <StickyNote className="size-2.5" />}
      </span>
      <div className="mb-1.5 flex flex-wrap items-center gap-2">
        <span
          className={cn(
            'text-3xs font-semibold uppercase tracking-widest',
            isGraph ? 'text-primary' : 'text-muted-foreground'
          )}
        >
          {t(isGraph ? 'investigations.page.note.graph' : 'investigations.page.note.investigation')}
        </span>
        {entry.noteKind && entry.noteKind !== 'observation' && (
          <Badge
            variant={entry.noteKind === 'assessment' ? 'default' : 'muted'}
            data-note-kind={entry.noteKind}
          >
            {entry.noteKind === 'assessment' ? <Scale aria-hidden /> : <ListTodo aria-hidden />}
            {t(`investigations.page.note.kinds.${entry.noteKind}`)}
          </Badge>
        )}
        {isGraph && attachedChips.length > 0 && (
          <>
            <span className="text-xs text-muted-foreground">·</span>
            <div className="flex flex-wrap items-center gap-1">
              {attachedChips.map(e => (
                <span
                  key={e.id}
                  className="rounded-md border border-primary/40 bg-primary/10 px-1.5 py-0.5 font-mono text-2xs text-primary"
                >
                  {e.label}
                </span>
              ))}
            </div>
          </>
        )}
      </div>
      <p className="m-0 text-sm leading-relaxed text-foreground">{entry.text}</p>
      <div className="mt-1 flex items-center gap-2 text-2xs text-muted-foreground">
        <span>{entry.author}</span>
        <span className="inline-block size-1 rounded-full bg-border" />
        <span className="font-mono">
          {formatRelative(entry.createdAt, i18n.language, t('overview.justNow'))}
        </span>
      </div>
    </div>
  )
}

// ─── Objects-set panel ───────────────────────────────────────────────────────

const KIND_PALETTE_TEXT: Record<number, string> = {
  0: 'bg-primary',
  1: 'bg-success',
  2: 'bg-warning',
  3: 'bg-info',
  4: 'bg-muted-foreground',
}

export function ObjectsSetPanel({ inv }: { inv: Investigation }) {
  const { t } = useTranslation()
  const kindIndex = useMemo(() => {
    const map = new Map<string, number>()
    for (const node of inv.graphView.nodes) {
      const kind = nodeKind(node)
      if (!map.has(kind)) map.set(kind, map.size)
    }
    return map
  }, [inv.graphView.nodes])

  const entityCounts = useMemo(() => {
    const counts = new Map<string, { kind: string; label: string; count: number }>()
    for (const node of inv.graphView.nodes) {
      const key = `${nodeKind(node)}:${nodeLabel(node)}`
      const existing = counts.get(key)
      if (existing) {
        existing.count += 1
      } else {
        counts.set(key, { kind: nodeKind(node), label: nodeLabel(node), count: 1 })
      }
    }
    return Array.from(counts.values()).sort((a, b) => b.count - a.count)
  }, [inv.graphView.nodes])

  if (inv.graphView.nodes.length === 0 && !inv.graphView.snippets?.length) {
    return (
      <p className="text-sm italic text-muted-foreground">
        {t('investigations.page.featured.noObjects')}
      </p>
    )
  }

  const visible = entityCounts.slice(0, 6)
  const hidden = entityCounts.length - visible.length

  return (
    <div className="space-y-1">
      {inv.graphView.snippets?.map(snippet => (
        <div key={snippet.id} className="rounded-md px-2 py-1.5">
          <p className="truncate text-sm">{snippet.title}</p>
          <p className="text-xs text-muted-foreground">
            Source snippet · {snippet.snapshot.result.rows.length} rows
          </p>
        </div>
      ))}
      {visible.map(entity => {
        const idx = kindIndex.get(entity.kind) ?? 0
        return (
          <div
            key={`${entity.kind}-${entity.label}`}
            className="flex items-center gap-3 rounded-md px-2 py-1.5"
          >
            <span
              className={cn(
                'size-2 shrink-0 rounded-full',
                KIND_PALETTE_TEXT[idx % 5] ?? 'bg-muted-foreground'
              )}
            />
            <div className="min-w-0 flex-1 overflow-hidden">
              <div className="truncate text-sm text-foreground">{entity.label}</div>
              <div className="truncate text-3xs uppercase tracking-wider text-muted-foreground">
                {entity.kind}
              </div>
            </div>
            <span className="shrink-0 font-mono text-2xs tabular-nums text-muted-foreground">
              {entity.count}×
            </span>
          </div>
        )
      })}
      {hidden > 0 && (
        <p className="px-2 font-mono text-2xs text-muted-foreground">
          {t('investigations.page.featured.moreEntities', { count: hidden })}
        </p>
      )}
    </div>
  )
}

// ─── Compact grid card ───────────────────────────────────────────────────────

export interface InvestigationCardProps {
  inv: Investigation
  index: number
  /** Destination URL when the card is clicked (e.g. the detail page). */
  to: string
  /** Optional state passed to the destination's `useLocation().state`. */
  state?: unknown
  /** When provided, a three-dots menu appears in the card and exposes a
   *  Delete action that fires this callback. The parent owns the
   *  confirmation flow + actual mutation. */
  onDelete?: () => void
}

function summaryFromNotes(inv: Investigation): string | null {
  if (inv.notes.length > 0) return inv.notes[0].text
  const first = inv.graphView.view.notes[0]
  return first ? first.text : null
}

export function InvestigationCard({ inv, index, to, state, onDelete }: InvestigationCardProps) {
  const { t, i18n } = useTranslation()
  const justNow = t('overview.justNow')
  return (
    <Card
      asChild
      interactive
      className="group/card relative flex min-h-[200px] flex-col gap-5 p-4 text-left"
    >
      <Link to={to} state={state}>
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-xs tabular-nums text-muted-foreground">
            {chapterNumber(index)}
          </span>
          <span className="flex-1" />
          <InvestigationSourcePill inv={inv} />
          {onDelete && (
            <div
              // The card itself is a <Link>; stopPropagation on the menu wrapper
              // keeps clicks on the trigger / items from bubbling up into a
              // navigation. preventDefault on the trigger handles the Link too.
              onClick={e => e.stopPropagation()}
            >
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label={t('investigations.page.card.menuLabel')}
                    onClick={e => e.preventDefault()}
                    className="flex size-9 items-center justify-center rounded-md border border-border bg-card/80 text-muted-foreground opacity-0 transition-opacity hover:bg-accent hover:text-foreground group-hover/card:opacity-100 focus-visible:opacity-100 md:size-7 [@media(hover:none)]:opacity-100"
                  >
                    <MoreHorizontal className="size-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-max md:w-44">
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onSelect={e => {
                      e.preventDefault()
                      onDelete()
                    }}
                  >
                    <Trash2 className="mr-2 size-3.5" />
                    {t('investigations.page.card.delete')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>

        <div>
          <div className="mb-1 line-clamp-2 text-sm font-semibold text-foreground md:line-clamp-1">
            {inv.title}
          </div>
          <div className="line-clamp-2 text-xs leading-relaxed text-muted-foreground">
            {summaryFromNotes(inv) ?? t('investigations.page.featured.noNotes')}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <GridSnapshotPill nodes={inv.graphView.nodes} />
          <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 font-mono text-xs tabular-nums text-muted-foreground">
            <StickyNote className="size-2.5 text-muted-foreground" />
            {inv.notes.length}
          </span>
          {inv.includedInReport && (
            <span className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
              <Star className="size-2.5" />
              {t('investigations.page.card.report')}
            </span>
          )}
        </div>

        <div className="mt-auto flex items-center gap-2 border-t border-border pt-4 text-2xs text-muted-foreground">
          <span className="text-foreground/80">{inv.authorName}</span>
          <span className="inline-block size-1 rounded-full bg-border" />
          <span className="font-mono">{formatRelative(inv.updatedAt, i18n.language, justNow)}</span>
          <span className="flex-1" />
          <ArrowRight className="size-3 transition-transform group-hover/card:translate-x-0.5" />
        </div>
      </Link>
    </Card>
  )
}
